import { Modal, message, Select, Button } from "antd";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { putRequest, getRequest } from "../../service";
import { getEmployeeData } from "../../utils/localStorage";
import { getChangedFields, pickFields } from "../../utils/getChangedFields";
import { salary as salaryFields } from "../../utils/form-keys";
import { CreditCard } from "lucide-react";
import { countries } from "../../constant/countery";

const SALARY_FIELD_KEYS = Object.keys(salaryFields);

const SalaryDetailsEdit = (props) => {
  const { open, onRequestClose, employee } = props;
  const [form, setForm] = useState({
    salary_type: "monthly",
    monthly_salary: "",
    day_calculation: "working",
    fixed_Days: "",
    perday_salary: "",
    perhour_salary: "",
    hourly_rate: "",
    paid_type: "",
    total_ammount: "",
    paid_ammount: "",
    advance_payment: "",
    salary_additional: [],
    allowance: "0",
    deduction: "0",
    contribution: "0",
    overAll_salary: "0"
  });

  const [salaryAdditional, setSalaryAdditional] = useState([]);
  const [selectedAdditions, setSelectedAdditions] = useState([]);
  const originalFormRef = useRef({});
  // Get currency sign from user data and countries list
  const userData = getEmployeeData();
  const currency = userData?.currency;
  const country = countries.find(c => c.currency === currency);
  const currencySign = country ? country.currencySymbol : currency ;

  const tableHeadings = ["Name", "Rate", "Amount", "Type"];

  const salaryTypeOptions = useMemo(() => [
    { value: 'monthly', label: 'Monthly' },
    { value: 'one_time', label: 'One Time' },
    { value: 'hourly', label: 'Hourly' },
  ], []);

  const daysTypeOptions = useMemo(() => [
    { value: 'total', label: 'Total Days in Month' },
    { value: 'working', label: 'Working Days Only' },
    { value: 'fixed', label: 'Fixed Days' }
  ], []);

  const paidTypeOptions = useMemo(() => [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'on_finish', label: 'On Finish' }
  ], []);

  // Calculate working days in current month excluding weekends
  const getWorkingDays = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();

    let workingDays = 0;
    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, month, day);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  }, []);

  // Calculate per hour salary
  const calculatePerHourSalary = useCallback((monthlySalary, days) => {
    const workingHoursPerDay = 8;
    const totalWorkingHours = days * workingHoursPerDay;
    return (monthlySalary / totalWorkingHours).toFixed(2);
  }, []);

  const getTotalDays = useCallback(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }, []);

  const fetchSalaryAdditional = useCallback(async () => {
    try {
      const response = await getRequest('/api/taxandbonus');
      if (response && response.entries) {
        const formattedData = response.entries.map(item => ({
          value: item._id,
          label: item.title,
          type: item.type === 'deductions' ? 'Deductions': item.type === 'contributions' ? 'Contributions' : 'Allowances',
          amount: item.amount,
          amountType: item.amountType,
          period: "monthly",
          calculateType: "not_fixed",
        }));
        setSalaryAdditional(formattedData);
      }
    } catch (error) {
      console.error('Error fetching salary additional data:', error);
    }
  }, []);

  useEffect(() => {
    fetchSalaryAdditional();
  }, [fetchSalaryAdditional]);

  useEffect(() => {
    if (employee) {
      const initialData = pickFields(employee, SALARY_FIELD_KEYS);
      setForm((prev) => ({
        ...prev,
        ...initialData,
      }));
      originalFormRef.current = initialData;
    }
  }, [employee]);

  const calculateSalaryRates = useCallback(() => {
    if (form.monthly_salary && form.salary_type === 'monthly') {
      const monthlyAmount = parseFloat(form.monthly_salary);
      if (!isNaN(monthlyAmount)) {
        let days;
        if (form.day_calculation === 'fixed') {
          days = parseInt(form.fixed_Days) || 0;
        } else {
          days = form.day_calculation === 'total' ? getTotalDays() : getWorkingDays();
        }
        if (days > 0) {
          const dailyRate = (monthlyAmount / days).toFixed(2);
          const hourlyRate = calculatePerHourSalary(monthlyAmount, days);
          return { dailyRate, hourlyRate };
        }
      }
    }
    return null;
  }, [form.monthly_salary, form.salary_type, form.day_calculation, form.fixed_Days, getTotalDays, getWorkingDays, calculatePerHourSalary]);

  useEffect(() => {
    const rates = calculateSalaryRates();
    if (rates) {
      setForm(prev => ({
        ...prev,
        perday_salary: rates.dailyRate,
        perhour_salary: rates.hourlyRate
      }));
    }
  }, [calculateSalaryRates]);

  useEffect(() => {
    if (form.salary_additional && salaryAdditional.length > 0) {
      const selected = salaryAdditional.filter(item => 
        form.salary_additional.includes(item.value)
      );
      setSelectedAdditions(selected);
    }
  }, [form.salary_additional, salaryAdditional]);

  const handleChange = useCallback((value, name) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const calculateNetSalary = useCallback(() => {
    const grossSalary = parseFloat(form.monthly_salary) || 0;
    let totalAllowances = 0;
    let totalContributions = 0;
    let totalDeductions = 0;

    selectedAdditions.forEach(item => {
      const amount = parseFloat(item.amount);
      const calculatedAmount = item.amountType === 'percentage'
        ? (grossSalary * amount) / 100
        : amount;

      switch (item.type) {
        case 'Allowances':
          totalAllowances += calculatedAmount;
          break;
        case 'Contributions':
          totalContributions += calculatedAmount;
          break;
        case 'Deductions':
          totalDeductions += calculatedAmount;
          break;
      }
    });

    const netSalary = grossSalary + totalAllowances - totalContributions - totalDeductions;

    setForm(prev => ({
      ...prev,
      allowance: totalAllowances.toFixed(2),
      deduction: totalDeductions.toFixed(2),
      contribution: totalContributions.toFixed(2),
      overAll_salary: netSalary.toFixed(2)
    }));

    return {
      grossSalary,
      totalAllowances,
      totalContributions,
      totalDeductions,
      netSalary
    };
  }, [form.monthly_salary, selectedAdditions]);

  const updatedSalaryDetails = useMemo(() => {
    const calculations = calculateNetSalary();
    const details = [
      {
        label: "Gross Salary",
        value: calculations.grossSalary,
      }
    ];

    selectedAdditions.forEach(item => {
      const amount = parseFloat(item.amount);
      const calculatedAmount = item.amountType === 'percentage'
        ? (calculations.grossSalary * amount) / 100
        : amount;

      details.push({
        label: item.label,
        value: calculatedAmount,
        type: item.type
      });
    });

    details.push(
      {
        label: "Total Allowances",
        value: calculations.totalAllowances,
        type: "summary"
      },
      {
        label: "Total Contributions",
        value: calculations.totalContributions,
        type: "summary"
      },
      {
        label: "Total Deductions",
        value: calculations.totalDeductions,
        type: "summary"
      },
      {
        label: "Net Salary",
        value: calculations.netSalary,
        type: "final"
      }
    );

    return details;
  }, [calculateNetSalary, selectedAdditions]);

  const handleSubmit = useCallback(async () => {
    try {
      const currentSalaryData = pickFields(form, SALARY_FIELD_KEYS);
      const payload = getChangedFields(
        originalFormRef.current,
        currentSalaryData,
        { keys: SALARY_FIELD_KEYS }
      );

      if (Object.keys(payload).length === 0) {
        message.info("No changes to update");
        return;
      }

      const response = await putRequest(`/api/employees?employeeId=${employee._id}`, payload);
      if (response.message === 'Employee updated successfully (some related data could not be loaded)') {
        message.success('Employee updated successfully')
        onRequestClose();
      }
      else {
        message.error(response.message || "Employee not updated")
      }
    } catch (error) {
      console.error('Error updating employee:', error);
    }
  }, [form, employee, onRequestClose]);

  const renderSalaryFields = useCallback(() => {
    const currentSalaryType = form.salary_type || "";

    switch (currentSalaryType) {
      case 'hourly':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="hourlyRate" className="text-md">
                Hourly Rate
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "hourly_rate")}
                  value={form.hourly_rate || ""}
                />
              </div>
            </div>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidType" className="text-md">
                Paid Type
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={paidTypeOptions}
                value={form.paid_type || ""}
                onChange={(value) => handleChange(value, "paid_type")}
              />
            </div>
          </>
        );

      case 'monthly':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="daysType" className="text-md">
                Days Calculation
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={daysTypeOptions}
                value={form.day_calculation || ""}
                onChange={(value) => handleChange(value, "day_calculation")}
              />
            </div>

            {form.day_calculation === 'fixed' && (
              <div className="col-span-6 flex flex-col gap-1">
                <label htmlFor="fixedDays" className="text-md">
                  Number of Working Days
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <input
                  type="number"
                  className="border px-2 py-1 rounded-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "fixed_Days")}
                  value={form.fixed_Days || ""}
                  placeholder="i.e 22"
                  min="1"
                  max="31"
                />
              </div>
            )}

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="weekdaySalary" className="text-md">
                Monthly Salary
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "monthly_salary")}
                  value={form.monthly_salary || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="perDaySalary" className="text-md">
                Per Day Salary
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  value={form.perday_salary || ""}
                  disabled
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="perhour_salary" className="text-md">
                Per Hour Salary
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  value={form.perhour_salary || ""}
                  disabled
                />
              </div>
            </div>
          </>
        );

      case 'one_time':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="totalAmount" className="text-md">
                Total Amount
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "total_ammount")}
                  value={form.total_ammount || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidType" className="text-md">
                Paid Type
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={paidTypeOptions}
                value={form.paid_type || ""}
                onChange={(value) => handleChange(value, "paid_type")}
              />
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidAmount" className="text-md">
                Paid Amount
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "paid_ammount")}
                  value={form.paid_ammount || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="advancePayment" className="text-md">
                Advance Payment
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "advance_payment")}
                  value={form.advance_payment || ""}
                />
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  }, [form, handleChange, currencySign, daysTypeOptions, paidTypeOptions]);

  return (
    <Modal
      title="Salary Details"
      open={open}
      onCancel={onRequestClose}
      footer={null}
      width={800}
    >
     <div className="bg-white h-[60vh] overflow-auto pr-5">
     <div className=" bg-white ">
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-6 flex flex-col gap-1">
            <label htmlFor="salaryType" className="text-md">
              Salary Type
              <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
            </label>
            <Select
              className="w-full"
              options={salaryTypeOptions}
              value={form.salary_type || "monthly"}
              onChange={(value) => handleChange(value, "salary_type")}
            />
          </div>

          {renderSalaryFields()}
        </div>
      </div>

      {form.salary_type === 'monthly' && (
        <>
          <div className="flex flex-col gap-3 mt-8">
            <div className="flex items-center gap-3 w-full">
              <Select
                mode="multiple"
                showSearch
                className="w-full"
                placeholder="Search to Select"
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                options={salaryAdditional}
                value={form.salary_additional || []}
                onChange={(values) => {
                  handleChange(values, "salary_additional");
                }}
              />
            </div>

            <table className="w-full">
              <thead>
                <tr className="rounded-md bg-tablehead">
                  {tableHeadings.map((heading) => (
                    <th
                      key={heading}
                      className="px-4 py-2.5 text-md text-black"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedAdditions.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-2.5 text-md text-center">{item.label}</td>
                    <td className="px-4 py-2.5 text-md text-center">{item.amount}{item.amountType === 'percentage' ? '%' : currencySign}</td>
                    <td className="px-4 py-2.5 text-md text-center">
                      {item.amountType === 'percentage'
                        ? Math.round((form.monthly_salary * item.amount) / 100)
                        : item.amount} {currencySign}
                    </td>
                    <td className="px-4 py-2.5 text-md text-center">{item.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-form-bg  rounded-md px-4 flex flex-col gap-6 py-8 mt-5 relative">
              <div className="flex flex-col gap-1">
                {updatedSalaryDetails.map((salary, index) => (
                  <div
                    className={`w-full flex justify-between items-center ${salary.type === 'summary' ? 'text-gray-600' :
                      salary.type === 'final' ? 'text-green-600 font-bold' : ''
                      }`}
                    key={index}
                  >
                    <h4 className="text-md">{salary.label}</h4>
                    <span className={`text-md font-semibold ${salary.type === 'summary' ? 'text-gray-600' :
                      salary.type === 'final' ? 'text-green-600' : 'text-gray-400'
                      }`}>
                      {salary.value.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex items-center justify-end gap-3 my-4">
        <Button onClick={onRequestClose} >Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          className="bg-primary text-white"
        >
          Update Salary Details
        </Button>
      </div>
     </div>
    </Modal>
  );
};

export default SalaryDetailsEdit;
